import { useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import { blogPosts, blogCategories } from '@/mocks/blog';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';
import { useBodyScrollLock } from '@/hooks/useBodyScrollLock';
import JsonLd, { getWebPageSchema, getBreadcrumbSchema } from '@/utils/seo';

export default function BlogPage() {
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [activeCategory, setActiveCategory] = useState('All');
  const { ref: gridRef, isVisible } = useScrollAnimation(0.03);

  useBodyScrollLock(selectedImage !== null);

  const filteredPosts = activeCategory === 'All'
    ? blogPosts
    : blogPosts.filter((p) => p.category === activeCategory);

  return (
    <Layout>
      <JsonLd data={getWebPageSchema('Blog & Insights | Expert Property Tips & Guides | Infinite Consultant & Services', 'Expert tips guides and industry insights on property design construction and maintenance in Bangalore. Landscaping Interiors UPVC Waterproofing Cleaning advice.', 'https://infiniteconsultant.com/blog')} />
      <JsonLd data={getBreadcrumbSchema([{ name: 'Home', url: 'https://infiniteconsultant.com/' }, { name: 'Blog', url: 'https://infiniteconsultant.com/blog' }])} />
      {/* Hero */}
      <section className="relative h-[350px] md:h-[450px] w-full overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Modern%20Indian%20creative%20workspace%20Bangalore%20with%20architectural%20magazines%20design%20books%20plant%20samples%20mood%20boards%20on%20a%20clean%20wooden%20desk%20natural%20light%20bright%20airy%20Indian%20professional%20interior%20photography&width=1600&height=800&seq=blog-hero&orientation=landscape&nocache=true"
          alt="Blog & News"
          className="absolute inset-0 w-full h-full object-cover object-top animate-reveal"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-foreground-950/60 via-foreground-950/40 to-foreground-950/70" />
        <div className="absolute inset-0 flex items-end">
          <div className="w-full px-4 sm:px-6 lg:px-10 pb-10 md:pb-14">
            <div className="max-w-6xl mx-auto">
              <nav className="flex items-center gap-2 text-white/80 text-sm mb-4" aria-label="Breadcrumb">
                <Link to="/" className="hover:text-white transition-colors whitespace-nowrap cursor-pointer">Home</Link>
                <span className="text-white/50">/</span>
                <span className="text-white whitespace-nowrap">Blog</span>
              </nav>
              <h1 className="font-heading font-bold text-2xl md:text-4xl lg:text-5xl text-white mb-3" style={{ opacity: 0, animation: 'fadeUpSoft 0.7s cubic-bezier(0.16, 1, 0.3, 1) 0.1s forwards' }}>
                Blog &amp; Insights
              </h1>
              <p className="text-sm md:text-lg text-white/80 max-w-2xl" style={{ opacity: 0, animation: 'fadeUpSoft 0.7s cubic-bezier(0.16, 1, 0.3, 1) 0.25s forwards' }}>
                Expert tips, guides, and industry insights on property design, construction, and maintenance.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Blog Grid */}
      <section className="py-14 md:py-20 bg-background-50">
        <div className="w-full px-4 sm:px-6 lg:px-10">
          <div className="max-w-6xl mx-auto">
            <div className="flex flex-wrap justify-center gap-2 mb-10">
              {blogCategories.map((category) => (
                <button
                  key={category}
                  onClick={() => setActiveCategory(category)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 whitespace-nowrap cursor-pointer ${
                    activeCategory === category
                      ? 'bg-gradient-brand text-white'
                      : 'bg-white text-foreground-700 border border-background-200/70 hover:border-primary-300 hover:text-primary-600'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>

            <div ref={gridRef} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {filteredPosts.map((post, index) => (
                <article
                  key={post.id}
                  className={`bg-white rounded-xl overflow-hidden border border-background-200/70 hover:shadow-sm transition-all duration-500 group ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                  style={{ transitionDelay: `${index * 0.08}s` }}
                >
                  <button
                    onClick={() => setSelectedImage(post.image)}
                    className="w-full text-left cursor-pointer"
                  >
                    <div className="relative h-48 md:h-56 overflow-hidden">
                      <img
                        src={post.image}
                        alt={post.title}
                        loading="lazy"
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                      />
                      <div className="absolute top-3 left-3">
                        <span className="px-2.5 py-1 rounded-md bg-white/90 backdrop-blur-sm text-xs font-medium text-foreground-800">
                          {post.category}
                        </span>
                      </div>
                    </div>
                  </button>
                  <div className="p-5 md:p-6">
                    <div className="flex items-center gap-3 text-xs text-foreground-400 mb-3">
                      <span>{post.date}</span>
                      <span>&middot;</span>
                      <span>{post.readTime}</span>
                    </div>
                    <h2 className="font-heading font-semibold text-base md:text-lg text-foreground-950 mb-3 leading-tight group-hover:text-primary-500 transition-colors">
                      {post.title}
                    </h2>
                    <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                      {post.excerpt}
                    </p>
                    <span className="inline-flex items-center gap-1 text-sm font-medium text-primary-500 hover:text-primary-600 transition-colors cursor-pointer">
                      Read More
                      <div className="w-3 h-3 flex items-center justify-center">
                        <i className="ri-arrow-right-line text-xs" />
                      </div>
                    </span>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Lightbox */}
      {selectedImage && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4 cursor-pointer animate-fade-in"
          onClick={() => setSelectedImage(null)}
        >
          <button
            onClick={() => setSelectedImage(null)}
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-white/20 transition-colors cursor-pointer"
          >
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-close-line text-xl" />
            </div>
          </button>
          <img
            src={selectedImage}
            alt="Blog image preview"
            className="max-w-full max-h-[90vh] rounded-xl object-contain animate-scale-in"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </Layout>
  );
}